import { type RefObject, useEffect, useState } from "react";

/**
 * Returns a tabIndex that makes a scroll container reachable by keyboard only
 * while its content overflows and nothing inside it can take focus (WCAG 2.1.1).
 */
export function useKeyboardScrollable(ref: RefObject<HTMLElement | null>) {
  const [tabIndex, setTabIndex] = useState<0 | undefined>(undefined);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const update = () => {
      const overflowing =
        element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
      const hasFocusable = !!element.querySelector(
        "a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      setTabIndex(overflowing && !hasFocusable ? 0 : undefined);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    for (const child of Array.from(element.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [ref]);

  return tabIndex;
}
